import { resolveUserFeatureFlags } from '../utils/resolve-feature-flags/resolve-feature-flags';
import {
  flagDefinitions,
  type FeatureFlags,
  type FeatureFlagValueType,
} from './feature-flags';
import { fetchFeatureFlagDefinitions } from './fetch-feature-flag-definitions';

/**
 * Non-hook, one-shot resolution of a single feature flag.
 *
 * Mirrors {@link useFeatureFlag} for callers that live outside the React tree
 * (e.g. module-level bootstrapping) and therefore cannot use react-query. It
 * fetches the server definitions, applies any localStorage overrides, and
 * gracefully returns `false` when the flag is missing or the request fails.
 */
export async function resolveFeatureFlag<T extends FeatureFlags>(
  flagName: T
): Promise<FeatureFlagValueType<T> | false> {
  try {
    const { status, data } = await fetchFeatureFlagDefinitions([flagName]);
    if (status !== 200) {
      return false;
    }

    const serverMatch = data.results.find(
      (serverFlag) => serverFlag.name === flagName
    );

    return resolveUserFeatureFlags(
      flagName,
      flagDefinitions[flagName].schema,
      serverMatch?.value
    );
  } catch {
    // Gracefully turn the feature off on any network/parse error.
    return false;
  }
}
